import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { OPERATION, TABLE_CELL_WIDTH, SORT_TYPE, COMPONENTS, CONSTANTS, TEMP_LOG_OFFICE_STATUS } from 'src/app/dataModels/constants';
import { CONFIG } from 'src/app/dataModels/config';
import { HelperService } from 'src/app/services/utils/helper.service';
import { ResourceManagerService } from 'src/app/services/resourceManager/resource-manager.service';
import { LOV_KEYS } from 'src/app/dataModels/lovs';
import { CompAccessService } from 'src/app/services/compAccess/comp-access.service';
import { lastValueFrom } from 'rxjs';
/**
 * Serive for temperature log
 */
@Injectable({
  providedIn: 'root',
})
export class TemperatureLogService {
  /**
   * minimum accepted temperature value  for freezer
   */
  FREEZER_MIN_TEMP = -58;

  /**
   * maximum accepted temperature value for freezer
   */
  FREEZER_MAX_TEMP = 5;

  /**
   * minimum accepted temperature value for refrigerator
   */
  REFRIGERATOR_MIN_TEMP = 35.6;

  /**
   * maximum accepted temperature value for refrigerator
   */
  REFRIGERATOR_MAX_TEMP = 46.4;


  ULTRA_COLD_FREEZER_MIN_TEMP = -130;
  ULTRA_COLD_FREEZER_MAX_TEMP = -76;

  THERMAL_SHIPPER_MIN_TEMP =  -130;
  THERMAL_SHIPPER_MAX_TEMP = -76;

  /**
   * constructor to inject dependency
   * @param apiService instance of apiService
   * @param helperService instance of helperService
   * @param resourceManagerService instance of resourceManagerService
   * @param compAccessService instance of compAccessService
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private resourceManagerService: ResourceManagerService,
    private compAccessService: CompAccessService
  ) {}

  /**
   * get active storage unit headers
   */
  getActiveStorageUnitHeaders() {
    return [
      {
        text: 'Unit Name',
        field: 'unitName',
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
      {
        text: 'Unit Type',
        field: 'unitTypeDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Unit Style',
        field: 'unitStyleDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Will COVID-19 Vaccine be stored in this Unit?',
        field: 'covidVaccineStoredDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Last Log Date',
        field: 'lastLogDate',
        sortType: SORT_TYPE.DATE,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Unit DeActivation Date',
        field: 'dateDeactivation',
        sortType: SORT_TYPE.DATE,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Record Temperatures',
        field: '',
        cssClass: `${TABLE_CELL_WIDTH.SMALLER}`,
        key: 'recordTemp',
        cellTemplate: 'RECORD_TEMP',
        noSort: true,
      },
      {
        text: 'View Logs',
        field: '',
        cssClass: `${TABLE_CELL_WIDTH.SMALLER}`,
        cellTemplate: 'VIEW',
        noSort: true,
      },
    ];
  }

  /**
   * get active storage unit headers
   */
  getInActiveStorageUnitHeaders() {
    return [
      {
        text: 'Unit Name',
        field: 'unitName',
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
      {
        text: 'Unit Type',
        field: 'unitTypeDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Unit Style',
        field: 'unitStyleDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Will COVID-19 Vaccine be stored in this Unit?',
        field: 'covidVaccineStoredDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Last Log Date',
        field: 'lastLogDate',
        sortType: SORT_TYPE.DATE,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Unit DeActivation Date',
        field: 'dateDeactivation',
        sortType: SORT_TYPE.DATE,
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'View Logs',
        field: '',
        cssClass: `${TABLE_CELL_WIDTH.ACTION}`,
        cellTemplate: 'VIEW',
      },
    ];
  }

  /**
   * check if user has access to manage and view temperature log
   */
  checkTempLogAcess() {
    return this.compAccessService.checkAccess([COMPONENTS.TEMPERATURELOG.MANAGE_UNIT, COMPONENTS.TEMPERATURELOG.MANAGE_TEMPERATURE_LOG]);
  }

  /**
   * get storage units
   * @param facilityId current facility Id of the logged in user
   */
  async getStorageUnits(facilityId) {
    const request = {
      objects: [
        {
          name: OPERATION.TEMPERATURELOG.GET_FACILITY_STORAGE_UNIT,
          params: {
            facilityId,
          },
        },
      ],
    };

    const storageUnits = {
      activeStorageUnits: [],
      inactiveStorageUnits: [],
    };

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      const storageUnitList = result.objects[0].json;
      this.formatStorageUnitList(storageUnitList);
      storageUnits.activeStorageUnits = storageUnitList.filter((x) => x.status === '1');
      storageUnits.inactiveStorageUnits = storageUnitList.filter((x) => x.status === '2');
    }

    return storageUnits;
  }

    /**
   * get resources
   * 1. LOV_KEYS.STORAGE_UNIT_TYPE
   * 2. LOV_KEYS.STORAGE_UNIT_STYLE
   */
    formatStorageUnitList(storageUnitList) {
      const request = [
        { key: LOV_KEYS.STORAGE_UNIT_TYPE },
        { key: LOV_KEYS.STORAGE_UNIT_STATUS },
        { key: LOV_KEYS.STORAGE_UNIT_STYLE }
      ];
  
      this.resourceManagerService.getMSResources(request,CONFIG.MS_GET_GLOBAL_BUNDLE).then((response) => {
        if(response){
        for (const storage of storageUnitList) {
          storage.unitTypeDescr = this.getStorageProperties(LOV_KEYS.STORAGE_UNIT_TYPE, storage.unitType, 'descr', response);
          storage.unitStatusDescr = this.getStorageProperties(LOV_KEYS.STORAGE_UNIT_STATUS, storage.status, 'descr', response);
          storage.unitStyleDescr = this.getStorageProperties(LOV_KEYS.STORAGE_UNIT_STYLE, storage.unitStyle, 'descr', response);
          storage.covidVaccineStoredDescr = storage.covidVaccineStored === 'Y' ? 'Yes' : storage.covidVaccineStored === 'N' ? 'No' : '';
        }
        }
      });
    }


    /**
   * get LOV resource property
   * @param key LOV
   * @param lovColumnVal columnValue
   * @param propertyToReturn name of property to be returned
   * @param propertyToCheck name of property to check against, default is 'columnValue'
   */
    getStorageProperties(key, lovColumnVal, propertyToReturn, response, propertyToCheck = 'columnValue') {
      let result = '';
      if (response) {
        const lovList = response[key];
  
        result = this.resourceManagerService.getLovProperty(lovList, propertyToCheck, lovColumnVal, propertyToReturn);
      } else {
        result = '';
      }
      return result;
    }

  /**
   * get facilities temp logs
   * @param facilityId current facility id of the user
   * @param facilityCategory facility category of the facility
   */
  async getFacilityTemperatureLog(facilityId, facilityCategory, fundType?) {
    const request = {
      objects: [
        {
          name: OPERATION.TEMPERATURELOG.GET_FACILITY_TEMPERATURE_LOG,
          params: {
            facilityId,
            facilityCategory,
          },
        },
      ],
    };

    let tempLogList = [];

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      tempLogList = result.objects[0].json;
      if (tempLogList) {
        tempLogList = this.sortTemperatureLogList(tempLogList);
        this.modifyTemperatureLogList(tempLogList,fundType);
      }
    }

    return tempLogList;
  }

  /**
   * sort temperature log list
   * @param temperatureLogList list of temperature logs
   */
  sortTemperatureLogList(temperatureLogList) {
    return temperatureLogList.sort((a, b) => {
      const dt1 = new Date(a.logPeriodFrom);
      const dt2 = new Date(b.logPeriodFrom);
      return dt1.setHours(0, 0, 0, 0) > dt2.setHours(0, 0, 0, 0) ? -1 : dt1.setHours(0, 0, 0, 0) < dt2.setHours(0, 0, 0, 0) ? 1 : 0;
    });
  }

  /**
   * modify temperature logs list
   * @param temperatureLogList list of temperature logs
   */
  modifyTemperatureLogList(temperatureLogList,fundType?) {
    
    temperatureLogList.forEach((temperatureLog, i) => {
      temperatureLog.serialNum = i;
      /*temperatureLog.unitTypeDescr = this.resourceManagerService.getLovColumnProperties(
        fundType === 'vfc' ? LOV_KEYS.VFC_VACC_STORAGE_UNIT_TYPE: fundType === 'statefund'? LOV_KEYS.STATE_FUND_VACC_STORAGE_UNIT_TYPE : LOV_KEYS.COVID_VACC_STORAGE_UNIT_TYPE,
        temperatureLog.unitType,
        'descr'
      );*/
      temperatureLog.unitTypeDescr = this.resourceManagerService.getLovColumnProperties(
        LOV_KEYS.VFC_VACC_STORAGE_UNIT_TYPE,
        temperatureLog.unitType,
        'descr'
      );
      temperatureLog.logPeriod = `${temperatureLog.logPeriodFrom} - ${temperatureLog.logPeriodTo}`;
      if (this.helperService.IsNullOrEmpty(temperatureLog.submitionDate)) {
        temperatureLog.submitionDate = 'NOT SUBMITTED';
      }
      if (!this.helperService.IsNullOrEmpty(temperatureLog.lastLogDate)) {
        const logPeriodTo = new Date(temperatureLog.logPeriodTo);
        const lastLogDate = new Date(temperatureLog.submitionDate);

        if (lastLogDate.setHours(0, 0, 0, 0) >= logPeriodTo.setHours(0, 0, 0, 0)) {
          temperatureLog.lastLogDate = temperatureLog.lastLogDate;
          temperatureLog.logSubmitted = temperatureLog.submitionDate;
        } else {
          temperatureLog.lastLogDate = 'NOT SUBMITTED';
          temperatureLog.logSubmitted = 'NOT SUBMITTED';
        }
      }
      temperatureLog.outOfRangeForModerna =
        temperatureLog.unitTypeDescr === 'FREEZER' && temperatureLog.covidOutOfRange === 'M'
          ? 'No'
          : temperatureLog.unitTypeDescr === 'FREEZER'
          ? 'Yes'
          : 'N/A';
    });
  }

  /**
   * check if the entered storage unit name is present in system or not
   * @param facilityId current facility Id of the logged in user
   * @param unitName unit name being entered
   */
  async checkStorageUnitName(facilityId, unitName) {
    const request = {
      facilityId,
      unitName,
    };

    let isUnitNameAvailable = '';

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.CHECK_STORAGE_UNITNAME, request));

    if (result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING) {
      isUnitNameAvailable = result.nameAvailable;
    }

    return isUnitNameAvailable;
  }

  /**
   * Manage Storage Unit
   * @param action action being performed ADD, UPDATE
   * @param storageUnitFormValues storage unit form values
   * @param storageRowData row data being UPDATED
   * @param facilityId facility Id for which storage unit is being managed
   */
  async manageStorageUnit(action, storageUnitFormValues, storageRowData, facilityId) {
    const request = {
      action,
      facilityId,
      unitName: storageUnitFormValues.unitName.toUpperCase(),
      unitType: storageUnitFormValues.unitType,
      unitStyle: storageUnitFormValues.unitStyle,
      dateActivation: this.helperService.formatDate(storageUnitFormValues.firstLogDate),
      isCovidVaccineStored: storageUnitFormValues.unitStoresCovidVaccine,
    } as any;

    if (action === CONSTANTS.UPDATE) {
      request.unitSeq = storageRowData.unitSeq;
      request.dateDeactivation = this.helperService.formatDate(storageUnitFormValues.deactivationDate);
    }

    return await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_STORAGE_UNIT, request));
  }

  /**
   * delete storage unit
   * @param unitSeq storage unitseq being deleted
   */
  async deleteStorageUnit(unitSeq) {
    const request = {
      action: CONSTANTS.DELETE,
      unitSeq,
    };

    return await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_STORAGE_UNIT, request));
  }

  /**
   * get the logging period for the storage unit
   * @param facilityId current facility id of the logged in user
   * @param storageUnitRow storage unit row for which temp is being recorded
   */
  async getFacilityTemperatureLogPeriod(facilityId, storageUnitRow) {
    const request = {
      objects: [
        {
          name: OPERATION.TEMPERATURELOG.GET_FACILITY_TEMPERATURE_LOG_PERIOD,
          params: {
            facilityId,
            unitSeq: storageUnitRow?.unitSeq,
          },
        },
      ],
    };

    let tempLogPeriodList = [];

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      const sortedResponseTempLogPeriodList = result.objects[0].json.sort((a, b) => {
        const dt1 = new Date(a.logDate);
        const dt2 = new Date(b.logDate);
        return dt1.setHours(0, 0, 0, 0) < dt2.setHours(0, 0, 0, 0) ? -1 : dt1.setHours(0, 0, 0, 0) > dt2.setHours(0, 0, 0, 0) ? 1 : 0;
      });

      tempLogPeriodList = this.fillTempLogPeriodList(sortedResponseTempLogPeriodList, storageUnitRow);
    }

    return tempLogPeriodList;
  }

  /**
   * fill the ds for temp log period list
   * @param tempLogPeriodList list of temp log periods
   * @param storageUnitRow storage unit row for which temp is being recorded
   */
  fillTempLogPeriodList(tempLogPeriodList, storageUnitRow) {
    const newTempLogPeriodList = [];
    const todayDt = new Date().setHours(0, 0, 0, 0);
    let activationDate;
    let deactivationDate;

    if (!this.helperService.IsNullOrEmpty(storageUnitRow.dateActivation)) {
      activationDate = new Date(storageUnitRow.dateActivation);
    }

    if (!this.helperService.IsNullOrEmpty(storageUnitRow.dateDeactivation)) {
      deactivationDate = new Date(storageUnitRow.dateDeactivation);
    }

    for (const tempLogPeriod of tempLogPeriodList) {
      const logDate = new Date(tempLogPeriod.logDate);
      if (activationDate && logDate.setHours(0, 0, 0, 0) >= activationDate.setHours(0, 0, 0, 0)) {
        newTempLogPeriodList.push({
          date: tempLogPeriod.logDate,
          isDateMoreThanToday: logDate.setHours(0, 0, 0, 0) > todayDt,
        });
        if (!this.helperService.IsNullOrEmpty(deactivationDate) && logDate.setHours(0, 0, 0, 0) === deactivationDate.setHours(0, 0, 0, 0)) {
          break;
        }
      }
    }
    return newTempLogPeriodList;
  }

  /**
   * get the previously saved or submitted temperature log details
   * @param logMasterSeq logMasterSeq
   */
  async getTemperatureLogDetails(logMasterSeq) {
    const request = {
      objects: [
        {
          name: OPERATION.TEMPERATURELOG.GET_FACILITY_TEMPERATURE_LOG_DETAILS,
          params: {
            logMasterSeq,
          },
        },
      ],
    };

    let savedTemperatureLogs = [];

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_IMODS_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      savedTemperatureLogs = result.objects[0].json;
    }

    return savedTemperatureLogs;
  }

  /**
   * Manage Temperature Log
   * @param action action being performed ADD or UPDATE
   * @param user  logged in user
   * @param logMasterSeq temp log seq
   * @param logStatus status of the temperature log DRAFT, SUBMITTED
   * @param storageRowData storage unit data
   * @param temperatureLogFormValues temperature log form values
   */
  async manageTemperatureLog(action, user, logMasterSeq, logStatus, storageRowData, temperatureLogFormValues) {
    console.log(storageRowData);
    const temperatureLogRequest = this.formTemperatureLogDetailListRequestAndCheckOutOfRange(
      storageRowData,
      temperatureLogFormValues.logFrmItems
    );
    console.log(temperatureLogRequest);
    const request = {
      action,
      facilityId: user.currentFacilityId,
      logMasterSeq,
      logStatus,
      logPeriodFrom: temperatureLogFormValues.logFrmItems[0].date.value,
      logPeriodTo: temperatureLogFormValues.logFrmItems[temperatureLogFormValues.logFrmItems.length - 1].date.value,
      unitName: storageRowData.unitName.toUpperCase(),
      isCovidVaccineStored: storageRowData.covidVaccineStoredDescr,
      unitType: storageRowData.unitType,
      unitSeq: storageRowData.unitSeq,
      temperatureLogDetailList: temperatureLogRequest.temperatureLogDetailList,
      comments: this.helperService.appendUserAndDateForComments(temperatureLogFormValues.comments, user.userName),
    } as any;

    if (logStatus === 'SUBMITTED') {
      this.determineToSendEmail(request, user, temperatureLogFormValues.comments, temperatureLogRequest.outOfRange);
    }

    return await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_TEMP_LOG, request));
  }

  /**
   * for VFC Facility, on entering comments or if temperature entered is out of range, then sendEmailFlag to 'Y', else 'N'
   * @param request request being formed
   * @param user logged in user
   * @param comments comments entered by user
   * @param outOfRange is temperature out of range
   */
  determineToSendEmail(request, user, comments, outOfRange) {
    if (!this.helperService.IsNullOrEmpty(user.currentVfcPin) && (comments.length > 0 || outOfRange)) {
      (request.sendEmailFlag = 'Y'),
        (request.facilityName = user.currentFacilityName),
        (request.vfcPin = user.currentVfcPin),
        (request.emailContentList = []);
      if (!this.helperService.IsNullOrEmpty(comments)) {
        request.emailContentList.push('TEMPERATURE_LOG_COMMENTS');
      }

      if (outOfRange) {
        request.emailContentList.push('TEMPERATURE_LOG');
      }
    } else {
      request.sendEmailFlag = 'N';
    }
  }

  /**
   * form request array for temperature log details
   */
  formTemperatureLogDetailListRequestAndCheckOutOfRange(storageRowData, temperatureLogFrmItems) {
    const temperatureLogRequest = {
      temperatureLogDetailList: [],
      outOfRange: false,
    };

    for (const temperatureLogFrm of temperatureLogFrmItems) {
      if (!this.helperService.IsNullOrEmpty(temperatureLogFrm.officeStatus)) {
        temperatureLogRequest.temperatureLogDetailList.push({
          logDate: temperatureLogFrm.date.value,
          officeStatus: temperatureLogFrm.officeStatus,
          logHours: temperatureLogFrm.hour,
          logMin: temperatureLogFrm.minute,
          logMinTemp: temperatureLogFrm.minTempInF,
          logMaxTemp: temperatureLogFrm.maxTempInF,
          staffName: temperatureLogFrm.staffInitials?.toUpperCase(),
        });
        if (!temperatureLogRequest.outOfRange) {
          temperatureLogRequest.outOfRange  =  this.checkIfTemperatureIsOutOfRange(
            storageRowData.unitTypeDescr,
            temperatureLogFrm.officeStatus,
            temperatureLogFrm.minTempInF,
            temperatureLogFrm.maxTempInF
          );
        }
      }
    }

    return temperatureLogRequest;
  }

  /**
   * check if temperature entered is out of range
   * @param unitType type of storage unit FREEZER or REFRIGERATOR
   * @param officeStatus status of the office
   * @param minTempInF  min temperature in Farenheit
   * @param maxTempInF max temperature in Farenheit
   */
  checkIfTemperatureIsOutOfRange(unitType, officeStatus, minTempInF, maxTempInF) {
    let tempInF;

    let outOfRange = false;
    if (unitType === 'FREEZER' && officeStatus === TEMP_LOG_OFFICE_STATUS.OPEN) {
      tempInF = +maxTempInF;
      if (tempInF > this.FREEZER_MAX_TEMP || tempInF < this.FREEZER_MIN_TEMP) {
        outOfRange = true;
      }

      tempInF = +minTempInF;

      if (tempInF > this.FREEZER_MAX_TEMP || tempInF < this.FREEZER_MIN_TEMP) {
        outOfRange = true;
      }
    } else if (unitType === 'REFRIGERATOR' && officeStatus === TEMP_LOG_OFFICE_STATUS.OPEN) {
      tempInF = +maxTempInF;

      if (tempInF > this.REFRIGERATOR_MAX_TEMP || tempInF < this.REFRIGERATOR_MIN_TEMP) {
        outOfRange = true;
      }

      tempInF = +minTempInF;

      if (tempInF > this.REFRIGERATOR_MAX_TEMP || tempInF < this.REFRIGERATOR_MIN_TEMP) {
        outOfRange = true;
      }
    } else if (unitType === 'ULTRA COLD FREEZER' && officeStatus === TEMP_LOG_OFFICE_STATUS.OPEN) {
      tempInF = +maxTempInF;

      if (tempInF > this.ULTRA_COLD_FREEZER_MAX_TEMP || tempInF < this.ULTRA_COLD_FREEZER_MIN_TEMP) {
        outOfRange = true;
      }

      tempInF = +minTempInF;

      if (tempInF > this.ULTRA_COLD_FREEZER_MAX_TEMP || tempInF < this.ULTRA_COLD_FREEZER_MIN_TEMP) {
        outOfRange = true;
      }
    } else if (unitType === 'THERMAL SHIPPER' && officeStatus === TEMP_LOG_OFFICE_STATUS.OPEN) {
      tempInF = +maxTempInF;

      if (tempInF > this.THERMAL_SHIPPER_MAX_TEMP || tempInF < this.THERMAL_SHIPPER_MIN_TEMP) {
        outOfRange = true;
      }

      tempInF = +minTempInF;

      if (tempInF > this.THERMAL_SHIPPER_MAX_TEMP || tempInF < this.THERMAL_SHIPPER_MIN_TEMP) {
        outOfRange = true;
      }
    }
    return outOfRange;
  }

  /**
   * get log period headers
   */
  getLogPeriodHeaders() {
    return [
      {
        text: 'Log Period',
        field: 'logPeriod',
        cellTemplate: 'LINK',
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
      {
        text: 'Log Submitted On',
        field: 'submitionDate',
        sortType: SORT_TYPE.DATE,
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
    ];
  }

  /**
   * check if user is admin
   * @param user logged in user
   */
  isUserAdmin(user) {
    return user?.profileType.toUpperCase() === 'VFC ADMIN';
  }

  /**
   * delete temperature log period
   * @param logMasterSeq logMasterSeq to be deleted
   * @param facilityId facility Id of the logged in user
   */
  async deleteTemperatureLogPeriod(logMasterSeq, facilityId) {
    const request = {
      action: 'DELETE',
      logMasterSeq,
      facilityId,
      logPeriodFrom: '',
      logPeriodTo: '',
      logStatus: '',
      comments: '',
      temperatureLogDetailList: [
        { logDate: '', logHours: '', logMin: '', logMinTemp: '', logMaxTemp: '', officeStatus: '', staffName: '' },
      ],
    };

    return await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_TEMP_LOG, request));
  }
}
